/**
 * StudioComposer — the one input used across Build and Test. Styled like the
 * former studio composer (quiet focus border, send button inside the field,
 * auto-growing textarea). Controlled: the parent owns the value. With `placeholders`, the
 * empty field writes them one after another (typed, held, erased) on a layer over the field with
 * its own blinking cursor that follows the letters (Robert, 18.10.2026: the field's real cursor
 * stood still at the start); the real cursor shows again with the first letter typed.
 * `placeholder` stays its label and what shows with reduced motion. `accessory` sits inside the field left of the send button
 * (Build: Mo's level, like ChatGPT's model picker); the text keeps clear of it.
 * With `voiceUrl` (the agent's /transcribe, Robert 03.11.2026) a microphone sits in the field left of the send button,
 * as in the widget: recording turns the field into the bar (cancel, the live wave, done in the send button's place) and
 * the text is added to what is already in the field (hooks/useVoiceInput.js).
 */
import { useEffect, useLayoutEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useVoiceInput } from '../hooks/useVoiceInput';

const TYPE_MS = 34;
const ERASE_MS = 14;
const HOLD_MS = 2300;
const GAP_MS = 380;

/** the ghost layer sits exactly where the textarea writes: same padding and type */
function matchGhost(ghost, el) {
  const cs = window.getComputedStyle(el);
  let ph = null;
  try { ph = window.getComputedStyle(el, '::placeholder'); } catch (_) { /* no pseudo-element styles here */ }
  ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'fontFamily', 'fontSize', 'fontWeight', 'lineHeight',
    'letterSpacing'].forEach((k) => { ghost.style[k] = cs[k]; });
  ghost.style.color = (ph && ph.color) || cs.color;
}

/** Writes the phrases letter by letter onto the ghost layer (no re-render per letter). */
function usePlaceholderLoop(ref, ghostRef, formRef, phrases, fallback, active) {
  const key = (phrases || []).join('\u0001');
  useEffect(() => {
    const el = ref.current;
    const ghost = ghostRef.current;
    const form = formRef.current;
    if (!el) return undefined;
    const list = key ? key.split('\u0001') : [];
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (!active || list.length < 2 || still || !ghost) {
      el.placeholder = fallback || '';
      return undefined;
    }
    const textEl = ghost.firstChild;
    const show = (text) => { textEl.textContent = text; };
    matchGhost(ghost, el);
    el.placeholder = '';
    form?.classList.add('has-ghost');
    let i = 0;
    let shown = list[0].length; // the first line stands complete, then the next ones are written
    let erasing = true;
    let timer = null;
    const tick = () => {
      const text = list[i];
      if (erasing) {
        shown -= 1;
        if (shown <= 0) {
          erasing = false;
          i = (i + 1) % list.length;
          shown = 0;
          show('');
          timer = setTimeout(tick, GAP_MS);
          return;
        }
        show(text.slice(0, shown));
        timer = setTimeout(tick, ERASE_MS);
        return;
      }
      if (shown < text.length) {
        shown += 1;
        show(text.slice(0, shown));
        timer = setTimeout(tick, TYPE_MS);
        return;
      }
      erasing = true;
      timer = setTimeout(tick, HOLD_MS);
    };
    show(list[0]);
    timer = setTimeout(tick, HOLD_MS);
    const refit = () => matchGhost(ghost, el);
    window.addEventListener('resize', refit);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', refit);
      show('');
      form?.classList.remove('has-ghost');
      el.placeholder = fallback || '';
    };
  }, [ref, ghostRef, formRef, key, fallback, active]);
}

function autoGrow(el) {
  if (!el) return;
  el.style.height = 'auto';
  const lineHeight = 22;
  const padding = 30;
  const minHeight = 52;
  const maxHeight = (lineHeight * 6) + padding;
  el.style.height = (el.value ? Math.max(minHeight, Math.min(el.scrollHeight, maxHeight)) : minHeight) + 'px';
  el.classList.toggle('has-overflow', el.scrollHeight > maxHeight);
}

const StudioComposer = forwardRef(function StudioComposer(
  { value, onChange, onSubmit, placeholder, placeholders = null, disabled = false, autoFocusInput = false, accessory = null,
    leading = null, className = '', voiceUrl = null },
  ref,
) {
  const innerRef = useRef(null);
  const formRef = useRef(null);
  const accRef = useRef(null);
  const ghostRef = useRef(null);
  const { i18n } = useTranslation();
  const valueRef = useRef(value);
  valueRef.current = value;
  const voice = useVoiceInput({
    url: voiceUrl,
    lang: i18n?.language,
    onText: (text) => {
      const prev = valueRef.current || '';
      onChange(prev.trim() ? `${prev.trimEnd()} ${text}` : text);
      requestAnimationFrame(() => {
        const el = innerRef.current;
        if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); autoGrow(el); }
      });
    },
  });
  // a notice of the microphone (blocked, nothing heard) stands in the empty field for a moment
  const shownPlaceholder = voice.notice || placeholder;
  usePlaceholderLoop(innerRef, ghostRef, formRef, placeholders, shownPlaceholder, !disabled && !value && !voice.notice);
  // the text stops before the accessory: its width (it changes with the chosen level) as a CSS variable
  const hasAccessory = !!accessory;
  useLayoutEffect(() => {
    const form = formRef.current;
    const acc = accRef.current;
    if (!form) return undefined;
    const measure = () => form.style.setProperty('--composer-accessory', acc?.offsetWidth ? `${acc.offsetWidth + 8}px` : '0px');
    measure();
    if (!acc || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(acc);
    return () => observer.disconnect();
  }, [hasAccessory]);
  useImperativeHandle(ref, () => ({
    focus: () => {
      const el = innerRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); autoGrow(el); }
    },
  }));
  // Re-measure when the value changes programmatically (template / adjust prefill).
  useEffect(() => { if (innerRef.current) autoGrow(innerRef.current); }, [value]);

  const submit = () => { if (!disabled && voice.state === 'idle' && (value || '').trim()) onSubmit(); };

  return (
    <form
      ref={formRef}
      className={`studio-hero-composer studio-composer${leading ? ' has-leading' : ''}${voice.supported ? ' has-mic' : ''}${
        voice.state !== 'idle' ? ' is-voice' : ''}${className ? ` ${className}` : ''}`}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
    >
      {leading && <div className="studio-composer-leading" aria-hidden="true">{leading}</div>}
      {placeholders && (
        <div className="studio-composer-ghost" ref={ghostRef} aria-hidden="true">
          <span className="studio-composer-ghost-text" /><span className="studio-composer-ghost-caret" />
        </div>
      )}
      <textarea
        ref={innerRef}
        rows={1}
        value={value}
        placeholder={shownPlaceholder}
        aria-label={placeholders ? placeholder : undefined}
        onChange={(e) => { onChange(e.target.value); autoGrow(e.target); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        disabled={disabled}
        aria-disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocusInput}
      />
      {accessory && <div className="studio-composer-accessory" ref={accRef}>{accessory}</div>}
      {voice.supported && (
        <button type="button" className="studio-composer-mic" onClick={voice.start} disabled={disabled}
          aria-label={voice.text.start} title={voice.text.start}>
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" />
          </svg>
        </button>
      )}
      {voice.state !== 'idle' && (
        <div className={`studio-composer-voice${voice.state === 'working' ? ' is-busy' : ''}`}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); voice.cancel(); } }}>
          <button type="button" className="studio-composer-voice-cancel" onClick={voice.cancel}
            aria-label={voice.text.cancel} title={voice.text.cancel}>
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          <canvas className="studio-composer-voice-wave" ref={voice.canvasRef} aria-hidden="true" />
          <span className="studio-composer-voice-status" role="status" aria-live="polite">
            {voice.state === 'working' ? voice.text.working : voice.text.recording}
          </span>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <button type="button" className="studio-composer-voice-done" onClick={voice.finish} autoFocus
            aria-label={voice.text.done} title={voice.text.done}>
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
          </button>
        </div>
      )}
      <button type="submit" className="studio-composer-send" disabled={disabled || !(value || '').trim()}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="send-icon">
          <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z" />
        </svg>
      </button>
    </form>
  );
});

export default StudioComposer;
